import { FormGroup, AbstractControl, FormControl, FormArray } from '@angular/forms';

/**
 * Utility functions for form handling.
 * Used across all master/detail forms and tables (invoices, vouchers, products, etc.).
 */
export class FormUtils {
  /**
   * True if the control is enabled and can be focused/edited by the user.
   * Skips disabled and readonly-like controls (e.g. date in Sales, price in returns, locked master fields).
   */
  private static isControlFocusable(control: AbstractControl): boolean {
    if (!control) return false;
    return !control.disabled && control.status !== 'DISABLED';
  }

  /**
   * Finds the first invalid control in a form group and focuses it.
   * Only considers focusable (enabled) controls so disabled master/detail fields never get focus.
   * @param formGroup The form group to validate
   * @returns true if an invalid control was found and focused, false otherwise
   */
  static focusFirstInvalidControl(formGroup: FormGroup): boolean {
    const invalidControls = this.getAllInvalidControls(formGroup);

    if (invalidControls.length === 0) {
      return false;
    }

    const firstInvalidControl = invalidControls[0];

    firstInvalidControl.markAsTouched();

    const controlName = this.getControlNameFromFormGroup(formGroup, firstInvalidControl);
    const controlPath = controlName === undefined ? this.getControlPath(formGroup, firstInvalidControl) : null;

    this.focusControl(firstInvalidControl, controlName, controlPath);

    return true;
  }

  /**
   * Gets all invalid controls that are focusable (enabled) from a form group, recursively.
   * Skips disabled controls in master and detail (tables): date, locked fields, disabled table cells, etc.
   * Applies to all forms using FormUtils (invoices, vouchers, products, branches, contacts, ...).
   * @param formGroup The form group to check
   * @returns Array of invalid controls that the user can actually fix
   */
  static getAllInvalidControls(formGroup: FormGroup): AbstractControl[] {
    const invalidControls: AbstractControl[] = [];

    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);

      if (control instanceof FormControl) {
        if (control.invalid && this.isControlFocusable(control)) {
          invalidControls.push(control);
        }
      } else if (control instanceof FormGroup) {
        if (this.isControlFocusable(control)) {
          invalidControls.push(...this.getAllInvalidControls(control));
        }
      } else if (control instanceof FormArray) {
        if (!this.isControlFocusable(control)) return;
        control.controls.forEach((item) => {
          if (item instanceof FormGroup) {
            if (this.isControlFocusable(item)) {
              invalidControls.push(...this.getAllInvalidControls(item));
            }
          } else if (item instanceof FormControl && item.invalid && this.isControlFocusable(item)) {
            invalidControls.push(item);
          }
        });
      }
    });

    return invalidControls;
  }

  /**
   * Attempts to focus a form control by finding its associated DOM element
   * @param control The form control to focus
   * @param controlName The name of the control (for lookup purposes)
   * @param controlPath Optional path when control is nested e.g. ['documentLines', 0, 'price']
   */
  static focusControl(control: AbstractControl, controlName?: string, controlPath?: (string | number)[] | null): void {
    // Wait for the view to update before trying to focus
    setTimeout(() => {
      let element: Element | null = null;

      // For controls inside FormArray (e.g. documentLines.0.quantity), try id-based lookup first
      if (!element && controlPath && controlPath.length === 3) {
        const [, index, name] = controlPath;
        const id = `${name}${index}`;
        element = document.getElementById(id);
      }

      if (controlName && !element) {
        // Try to find the element by looking for elements with the form control's name
        element = document.querySelector(`[formcontrolname="${controlName}"],
                                         [formGroupName] [formcontrolname="${controlName}"]`);
      }

      // If we didn't find it by name, try to find by other means
      if (!element) {
        element = this.findElementForControl(control, controlName);
      }

      if (element) {
        // For custom components like app-text-input-row, we need to focus the actual input inside
        let focusElement = element as HTMLElement;

        // If it's a custom component, try to find the actual input element inside it
        if (element.tagName.toLowerCase().startsWith('app-')) {
          const inputElement = element.querySelector('input, select, textarea, [contenteditable=true]');
          if (inputElement) {
            focusElement = inputElement as HTMLElement;
          }
        }

        focusElement.focus();

        // Scroll the element into view if needed
        focusElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 0);
  }

  /**
   * Helper method to get the name of a control from the form group (direct children only)
   * @param formGroup The parent form group
   * @param control The control to find the name for
   * @returns The name of the control or undefined
   */
  private static getControlNameFromFormGroup(formGroup: FormGroup, control: AbstractControl): string | undefined {
    for (const [name, ctrl] of Object.entries(formGroup.controls)) {
      if (ctrl === control) {
        return name;
      }
    }
    return undefined;
  }

  /**
   * Recursively finds the path to a control (e.g. ['documentLines', 0, 'price']) when it is nested inside FormArray/FormGroup
   * @param formGroup The root form group
   * @param control The control to find the path for
   * @returns Path array or null if not found
   */
  private static getControlPath(formGroup: FormGroup, control: AbstractControl): (string | number)[] | null {
    for (const [key, ctrl] of Object.entries(formGroup.controls)) {
      if (ctrl === control) {
        return [key];
      }
      if (ctrl instanceof FormGroup) {
        const nested = this.getControlPath(ctrl, control);
        if (nested) {
          return [key, ...nested];
        }
      }
      if (ctrl instanceof FormArray) {
        const arr = ctrl;
        for (let i = 0; i < arr.controls.length; i++) {
          const item = arr.at(i);
          if (item === control) {
            return [key, i];
          }
          if (item instanceof FormGroup) {
            const nested = this.getControlPath(item, control);
            if (nested) {
              return [key, i, ...nested];
            }
          }
        }
      }
    }
    return null;
  }

  /**
   * Tries to find the DOM element associated with a form control
   * @param control The form control
   * @param controlName The name of the control
   * @returns The DOM element or null if not found
   */
  private static findElementForControl(control: AbstractControl, controlName?: string): Element | null {
    // Find all elements that might be associated with form controls
    const allFormElements = document.querySelectorAll('input, select, textarea, [formcontrol], [formcontrolname]');

    // Try to find by matching the control reference or name
    for (const element of Array.from(allFormElements)) {
      const elementControlName = (element as HTMLElement).getAttribute('formcontrolname');

      // If we have a control name, try to match it
      if (controlName && elementControlName === controlName) {
        return element;
      }

      // For custom components, we'll try to match by looking for the control in the component
      // This is a more robust approach for custom components
      if (this.isControlAssociatedWithElement(control, element)) {
        return element;
      }
    }

    return null;
  }

  /**
   * Checks if a form control is associated with a DOM element
   * @param control The form control
   * @param element The DOM element
   * @returns True if the control is associated with the element
   */
  private static isControlAssociatedWithElement(control: AbstractControl, element: Element): boolean {
    // This is a simplified check - in a real scenario, you might need to use Angular's
    // internal mechanisms to check the association
    // For now, we'll rely on the attribute matching
    return true;
  }

  /**
   * Alternative implementation that works better with custom components
   * @param formGroup The form group to validate
   * @param controlSelectors A mapping of control names to their corresponding element selectors
   * @returns true if an invalid control was found and focused, false otherwise
   */
  static focusFirstInvalidControlAdvanced(formGroup: FormGroup, controlSelectors?: Record<string, string>): boolean {
    const invalidControls = this.getAllInvalidControls(formGroup);

    if (invalidControls.length === 0) {
      return false;
    }

    // Get the first invalid control
    const firstInvalidControl = invalidControls[0];

    // Mark as touched to ensure validation messages appear
    firstInvalidControl.markAsTouched();

    // Get the control name
    const controlName = this.getControlNameFromFormGroup(formGroup, firstInvalidControl);

    if (controlName && controlSelectors && controlSelectors[controlName]) {
      const element = document.querySelector(controlSelectors[controlName]);
      if (element) {
        (element as HTMLElement).focus();
        (element as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
        return true;
      }
    }

    // Fallback to generic approach
    this.focusControl(firstInvalidControl, controlName);
    return true;
  }
}